// src/redux/slices/caseStudyAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { caseStudiesService } from '../../services/caseStudiesService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchCaseStudiesAdmin = createAsyncThunk(
    'caseStudyAdmin/fetchCaseStudiesAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudies({
                ...params,
                limit: params.limit || 10,
                page: params.page || 1
            })
            return {
                caseStudies: response.data.caseStudies,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchCaseStudyCategories = createAsyncThunk(
    'caseStudyAdmin/fetchCaseStudyCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudyCategories()
            return response.data.categories
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchCaseStudyIndustries = createAsyncThunk(
    'caseStudyAdmin/fetchCaseStudyIndustries',
    async (_, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudyIndustries()
            return response.data.industries
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createCaseStudy = createAsyncThunk(
    'caseStudyAdmin/createCaseStudy',
    async (caseStudyData, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.createCaseStudy(caseStudyData)
            return response.data.caseStudy
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateCaseStudy = createAsyncThunk(
    'caseStudyAdmin/updateCaseStudy',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.updateCaseStudy(id, data)
            return response.data.caseStudy
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteCaseStudy = createAsyncThunk(
    'caseStudyAdmin/deleteCaseStudy',
    async (id, { rejectWithValue }) => {
        try {
            await caseStudiesService.deleteCaseStudy(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const caseStudyAdminSlice = createSlice({
    name: 'caseStudyAdmin',
    initialState: {
        caseStudies: [],
        currentCaseStudy: null,
        categories: [],
        industries: [],
        loading: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        error: null
    },
    reducers: {
        clearCurrentCaseStudy: (state) => {
            state.currentCaseStudy = null
        },
        clearError: (state) => {
            state.error = null
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Case Studies Admin
            .addCase(fetchCaseStudiesAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchCaseStudiesAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.caseStudies = action.payload.caseStudies
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchCaseStudiesAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Fetch Categories
            .addCase(fetchCaseStudyCategories.fulfilled, (state, action) => {
                state.categories = action.payload
            })
            
            // Fetch Industries
            .addCase(fetchCaseStudyIndustries.fulfilled, (state, action) => {
                state.industries = action.payload
            })
            
            // Create Case Study
            .addCase(createCaseStudy.pending, (state) => {
                state.loading = true
            })
            .addCase(createCaseStudy.fulfilled, (state, action) => {
                state.loading = false
                state.caseStudies.unshift(action.payload)
                state.error = null
            })
            .addCase(createCaseStudy.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Update Case Study
            .addCase(updateCaseStudy.pending, (state) => {
                state.loading = true
            })
            .addCase(updateCaseStudy.fulfilled, (state, action) => {
                state.loading = false
                const index = state.caseStudies.findIndex(caseStudy => caseStudy._id === action.payload._id)
                if (index !== -1) {
                    state.caseStudies[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateCaseStudy.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Delete Case Study
            .addCase(deleteCaseStudy.fulfilled, (state, action) => {
                state.caseStudies = state.caseStudies.filter(caseStudy => caseStudy._id !== action.payload)
                state.error = null
            })
    }
})

export const { clearCurrentCaseStudy, clearError, setPagination } = caseStudyAdminSlice.actions
export default caseStudyAdminSlice.reducer